import merge from 'lodash/merge';
import { useMemo } from 'react';
// @mui
import { ThemeProvider, createTheme, useTheme, alpha } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

//
import { useSettingsContext } from './SettingsContext';

// ----------------------------------------------------------------------

type ColorProps = {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
};

type Props = {
  colorSetting: {
    primary: ColorProps;
    secondary: ColorProps;
    article: ColorProps;
    textMainColor: string;
    subTextColor: string;
    buttonTextColor: string;
    pageBGColor: string;
    areaBGColor: string;
    borderColor: string;
    error: ColorProps;
    symbol: ColorProps;
    extDriver: ColorProps;
    extTextMain: ColorProps;
  };
  children: React.ReactNode;
};

export default function ThemeContrast({ colorSetting, children }: Props) {
  const outerTheme = useTheme();

  const { themeContrast, themeMode } = useSettingsContext();

  const isLight = themeMode === 'light';

  const isContrastBold = themeContrast === 'bold';

  // 拡張したテーマの色設定を取得
  const {
    primary, secondary, article,
    textMainColor,subTextColor, buttonTextColor, pageBGColor, areaBGColor, borderColor, error,
    symbol, extDriver, extTextMain
  } = colorSetting;

  const themeOptions = useMemo(
    () => ({
      palette: {
        primary,
        secondary,
        article,
        symbol,
        driver: extDriver,
        area: {
          background: areaBGColor === '' ? alpha(outerTheme.palette.grey[500], 0.04) : areaBGColor,
        },
        error,
        divider: borderColor === '' ? outerTheme.palette.divider : borderColor,
        textMain: extTextMain,
        text: {
          primary: textMainColor === '' ? outerTheme.palette.text.primary : textMainColor,
          secondary: subTextColor === '' ? undefined : subTextColor,
        },
        background: {
          default: pageBGColor,
        },
        button: {
          text: buttonTextColor === '' ? outerTheme.palette.text.primary : buttonTextColor
        }
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              color: buttonTextColor === '' ? outerTheme.palette.text.primary : buttonTextColor,
              '&.MuiButton-outlined': {
                color: primary.main,
                '&.Mui-disabled': {
                  color: outerTheme.palette.text.disabled,
                },
              },
              '&.MuiButton-contained:hover': {
                boxShadow: `0 8px 16px 0 ${alpha(primary.main, 0.24)}`,
              }
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              borderBottom: `1px dashed ${borderColor === '' ? outerTheme.palette.divider : borderColor} !important`,
            }
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: {
              background: pageBGColor,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              background: pageBGColor,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            ...(isContrastBold && {
              root: {
                boxShadow: outerTheme.customShadows.z4,
              },
            }),
          },
        },
      },
    }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLight, themeContrast]
  );

  const theme = createTheme(merge(outerTheme, themeOptions));

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
